import React from 'react';
import { object, string, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { StaticPage, TopbarContainer } from '..';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  MarketplaceProofStats,
  MultiFilterSearchBar,
  NamedLink,
  NamedRedirect,
  FAQ,
  SectionUsefulGuides,
} from '../../components';
import SectionNationalTypeListings from '../../components/SectionNationalTypeListings/SectionNationalTypeListings';
import SectionNationalTypeSnapshot from '../../components/SectionNationalTypeSnapshot/SectionNationalTypeSnapshot';
import SectionNationalTypeEditorial from '../../components/SectionNationalTypeEditorial/SectionNationalTypeEditorial';
import SectionNationalTypePopularListings from '../../components/SectionNationalTypePopularListings/SectionNationalTypePopularListings';
import {
  buildNationalTypeProofItems,
  getNationalTrailerTypeLandingPage,
} from '../../util/nationalTrailerTypeLandingPages';
import {
  buildNationalTrailerTypeBreadcrumbItems,
  createNationalTrailerTypeBreadcrumbSchema,
} from '../../util/trailerHireBreadcrumbs';
import { MARKETPLACE_COMPLETED_HIRE_REVIEWS_COUNT_DISPLAY } from '../../marketplace-custom-config';
import {
  hasResolvedPlaceFromField,
  formatHeroDatesQueryParam,
} from '../../forms/HeroLocationSearchForm/heroLocationSearchParams';
import {
  clearNearbyTypePreview,
  loadNearbyTypePreviewListings,
} from './NationalTrailerTypeLandingPage.duck';
import {
  trackNationalTypePageNearbyResults,
  trackNationalTypePageUseLocation,
} from '../../analytics/nationalTypePageEvents';
import { getAdviceArticle } from '../../util/adviceArticles';
import carTrailerImage from '../../components/SectionLocations/images/car-trailer.jpeg';

import pageCss from '../SuburbLandingPage/SuburbLandingPage.module.css';
import css from './NationalTrailerTypeLandingPage.module.css';

const renderBreadcrumbLink = item => {
  if (item.current || !item.link) {
    return (
      <span className={pageCss.breadcrumbCurrent} aria-current="page">
        {item.label}
      </span>
    );
  }

  return (
    <NamedLink name={item.link.name} params={item.link.params} className={pageCss.breadcrumbLink}>
      {item.label}
    </NamedLink>
  );
};

const getAdviceGuides = pageConfig =>
  (pageConfig.adviceSlugs || [])
    .map(slug => getAdviceArticle(slug))
    .filter(Boolean)
    .slice(0, 3)
    .map(article => ({
      id: article.slug,
      routeName: 'AdviceArticlePage',
      params: { slug: article.slug },
      label: article.title,
      summary: article.excerpt,
    }));

export const NationalTrailerTypeLandingPageComponent = props => {
  const {
    history,
    match,
    previewSnapshot = null,
    previewKey: statePreviewKey = null,
    onLoadNearbyListings = () => Promise.resolve(),
    onClearNearbyListings = () => {},
  } = props;
  const typeSlug = match?.params?.typeSlug;
  const pageConfig = getNationalTrailerTypeLandingPage(typeSlug);

  if (!pageConfig) {
    return <NamedRedirect name="NotFoundPage" />;
  }

  const {
    metaTitle,
    metaDescription,
    introParagraphs,
    pageHeading,
    searchHeading,
    searchCta,
    pubCategory,
    slug,
    typeName,
    faqs,
    faqHeading,
    adviceHeading,
    adviceIntro,
  } = pageConfig;

  const breadcrumbItems = buildNationalTrailerTypeBreadcrumbItems(slug);
  const breadcrumbSchema = createNationalTrailerTypeBreadcrumbSchema(slug);
  const isCurrentTypePage = statePreviewKey === slug;
  const typeSnapshot = isCurrentTypePage ? previewSnapshot : null;
  const proofItems = buildNationalTypeProofItems({
    listingCount: typeSnapshot?.totalCount,
    trailerTypeName: typeName,
    reviewCountDisplay: MARKETPLACE_COMPLETED_HIRE_REVIEWS_COUNT_DISPLAY,
  });
  const faqUiItems = (faqs || []).map(item => ({
    question: item.question,
    answer: <p>{item.answer}</p>,
  }));
  const faqSchema =
    faqs && faqs.length > 0
      ? {
          '@type': 'FAQPage',
          mainEntity: faqs.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;
  const pageSchema = [
    {
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      description: metaDescription,
      name: metaTitle,
    },
    breadcrumbSchema,
    faqSchema,
  ].filter(Boolean);
  const adviceGuides = getAdviceGuides(pageConfig);

  const handleSearchContextChange = ({ location, dates } = {}) => {
    if (!hasResolvedPlaceFromField(location)) {
      onClearNearbyListings();
      return;
    }

    const selectedPlace = location.selectedPlace;
    const locationName = (location.search || selectedPlace.address || '').split(',')[0].trim();
    if (!locationName) {
      onClearNearbyListings();
      return;
    }

    const datesParam = formatHeroDatesQueryParam(dates);
    const searchParams = {
      address: location.search || selectedPlace.address,
      origin: selectedPlace.origin,
      bounds: selectedPlace.bounds,
    };
    if (datesParam) {
      searchParams.dates = datesParam;
    }

    trackNationalTypePageUseLocation({ typeSlug: slug, source: 'search_form' });
    onLoadNearbyListings({
      pageConfig,
      origin: selectedPlace.origin,
      bounds: selectedPlace.bounds,
      locationName,
      searchParams,
      dates: datesParam,
    }).then(response => {
      if (response) {
        trackNationalTypePageNearbyResults({ typeSlug: slug });
      }
    });
  };

  return (
    <StaticPage
      title={metaTitle}
      description={metaDescription}
      schema={pageSchema}
      facebookImages={[{ url: carTrailerImage, width: 640, height: 480 }]}
      twitterImages={[{ url: carTrailerImage, width: 640, height: 480 }]}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={pageCss.root}>
          <div className={pageCss.hero}>
            {breadcrumbItems.length > 0 ? (
              <nav className={pageCss.breadcrumbs} aria-label="Breadcrumb">
                <ol className={pageCss.breadcrumbList}>
                  {breadcrumbItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {index > 0 ? (
                        <li className={pageCss.breadcrumbSeparatorItem} aria-hidden="true">
                          <span className={pageCss.breadcrumbSeparator} />
                        </li>
                      ) : null}
                      <li className={pageCss.breadcrumbItem}>{renderBreadcrumbLink(item)}</li>
                    </React.Fragment>
                  ))}
                </ol>
              </nav>
            ) : null}

            <h1 className={pageCss.pageTitle}>{pageHeading}</h1>
            <MarketplaceProofStats items={proofItems} />
            {introParagraphs.map(paragraph => (
              <p key={paragraph} className={pageCss.intro}>
                {paragraph}
              </p>
            ))}

            <section className={pageCss.searchSection} aria-label={searchHeading}>
              <h2 className={pageCss.searchHeading}>{searchHeading}</h2>
              <div className={pageCss.searchBar}>
                <MultiFilterSearchBar
                  history={history}
                  initialPubCategory={pubCategory}
                  lockTrailerType
                  hideTrailerTypeFilter
                  submitLabel={searchCta}
                  onSearchContextChange={handleSearchContextChange}
                />
              </div>
            </section>
          </div>

          <ul className={pageCss.sections}>
            <li className={pageCss.section}>
              <div className={pageCss.sectionContent}>
                <SectionNationalTypeListings pageConfig={pageConfig} />
              </div>
            </li>

            <li className={pageCss.section}>
              <div className={pageCss.sectionContent}>
                <SectionNationalTypeSnapshot pageConfig={pageConfig} previewKey={slug} />
                <SectionNationalTypePopularListings pageConfig={pageConfig} />
                <SectionNationalTypeEditorial pageConfig={pageConfig} />
              </div>
            </li>

            {adviceGuides.length > 0 ? (
              <li className={pageCss.section}>
                <div className={pageCss.sectionContent}>
                  <SectionUsefulGuides
                    headingId="national-type-advice"
                    headingMessageId="TrailerHirePage.guidesHeading"
                    introMessageId="TrailerHirePage.guidesIntro"
                    heading={adviceHeading}
                    intro={adviceIntro}
                    guides={adviceGuides}
                    showAdviceHubLink
                  />
                </div>
              </li>
            ) : null}

            {faqUiItems.length > 0 ? (
              <li className={pageCss.section}>
                <div className={pageCss.sectionContent}>
                  <section className={css.contentBlock} aria-labelledby="national-type-faq">
                    <h2 className={css.heading} id="national-type-faq">
                      {faqHeading}
                    </h2>
                    <FAQ items={faqUiItems} />
                  </section>
                </div>
              </li>
            ) : null}
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

NationalTrailerTypeLandingPageComponent.propTypes = {
  history: object.isRequired,
  match: object.isRequired,
  previewSnapshot: object,
  previewKey: string,
  onLoadNearbyListings: func,
  onClearNearbyListings: func,
};

const mapStateToProps = state => {
  const { previewSnapshot, previewKey } = state.NationalTrailerTypeLandingPage || {};
  return {
    previewSnapshot,
    previewKey,
  };
};

const mapDispatchToProps = dispatch => ({
  onLoadNearbyListings: params => dispatch(loadNearbyTypePreviewListings(params)),
  onClearNearbyListings: () => dispatch(clearNearbyTypePreview()),
});

const NationalTrailerTypeLandingPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(NationalTrailerTypeLandingPageComponent);

export default NationalTrailerTypeLandingPage;
